import { Check } from 'lucide-react';

import {
  formatMoney,
  priceFor,
  REPORT_TURNAROUND_SHORT,
  TURNAROUND_DISCLAIMER,
} from '@repo/contracts/services-pricing';

import { Card, Container, SectionHeading } from '../ui';

/**
 * Floto vs a traditional manual audit. Describes common practice, never a
 * named competitor or their prices — every Floto cell is something the
 * service actually does.
 */
const ROWS: { label: string; manual: string; floto: string }[] = [
  {
    floto: `Days. Full report ${REPORT_TURNAROUND_SHORT} after payment*`,
    label: 'Turnaround',
    manual: 'Usually weeks',
  },
  {
    floto: `${formatMoney(priceFor(1).ratePerPage)} per page, shown upfront`,
    label: 'Price',
    manual: 'Day rates, quoted per engagement',
  },
  {
    floto: 'Every page in scope',
    label: 'Coverage',
    manual: 'A sample of pages',
  },
  {
    floto: '220 checks on every page, then a specialist',
    label: 'Depth',
    manual: 'Depends on the auditor',
  },
  {
    floto: 'Same checks every time, benchmarked publicly',
    label: 'Consistency',
    manual: 'Varies between auditors',
  },
];

export function Comparison() {
  return (
    <section
      aria-labelledby="compare-title"
      className="py-24 md:py-32"
      id="compare"
    >
      <Container>
        <SectionHeading
          eyebrow="Why Floto"
          id="compare-title"
          intro="Specialist judgement where it counts, tooling for the legwork — so you get more coverage for a fraction of the cost and time."
          title="Why not a traditional audit?"
        />
        {/* Phones: one row per dimension, Floto first. A sideways-scrolling
            table would hide the column that matters. */}
        <ul className="mt-10 space-y-3 md:hidden">
          {ROWS.map((row) => (
            <li key={row.label}>
              <Card className="p-5">
                <p className="text-small-label uppercase text-text-secondary">
                  {row.label}
                </p>
                <p className="mt-2 flex items-start gap-2 text-ui-lg font-medium text-text-primary">
                  <Check
                    aria-hidden
                    className="mt-1 size-4 shrink-0 text-success-ink"
                  />
                  <span>
                    <span className="sr-only">Floto: </span>
                    {row.floto}
                  </span>
                </p>
                <p className="mt-1 pl-6 text-ui text-text-secondary">
                  Traditional audit: {row.manual}
                </p>
              </Card>
            </li>
          ))}
        </ul>
        <Card className="mt-12 hidden p-0 md:block">
          <table className="w-full text-left text-ui-lg">
            <caption className="sr-only">
              A traditional manual audit compared with Floto
            </caption>
            <thead>
              <tr className="border-b border-hairline text-small-label uppercase text-text-secondary">
                <td className="px-6 py-4" />
                <th className="px-6 py-4 font-semibold" scope="col">
                  Traditional manual audit
                </th>
                <th
                  className="bg-warm px-6 py-4 font-semibold text-text-primary"
                  scope="col"
                >
                  Floto
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr
                  className="border-b border-hairline last:border-0"
                  key={row.label}
                >
                  <th
                    className="px-6 py-4 font-semibold text-text-primary"
                    scope="row"
                  >
                    {row.label}
                  </th>
                  <td className="px-6 py-4 text-text-secondary">
                    {row.manual}
                  </td>
                  <td className="bg-warm px-6 py-4 text-text-primary">
                    <span className="flex items-start gap-2">
                      <Check
                        aria-hidden
                        className="mt-1 size-4 shrink-0 text-success-ink"
                      />
                      {row.floto}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <p className="mt-4 text-caption text-text-secondary">
          * {TURNAROUND_DISCLAIMER}
        </p>
      </Container>
    </section>
  );
}
